import cn from 'classnames';
import { Dialog } from '@reach/dialog';
import { ReactNode, useRef } from 'react';
import { Transition } from 'react-transition-group';
import { useTranslations } from 'next-intl';
import { useSwipeable } from 'react-swipeable';

import styles from './Sidebar.module.scss';

interface SidebarProps {
  children: ReactNode;
  label: string;
  open: boolean;
  onClose: () => void;
  side?: 'left' | 'right';
  classNames?: string;
  containerClassNames?: string | string[];
}

const Sidebar = ({
  children,
  label,
  open = false,
  onClose,
  side = 'right',
  classNames,
  containerClassNames,
}: SidebarProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const transitionTime = 300;
  const swipeHandler = useSwipeable({
    trackMouse: true,
    onSwipedRight: () => onClose(),
  });

  const t = useTranslations('common');

  return (
    <Transition
      nodeRef={nodeRef}
      in={open}
      timeout={{
        enter: 0,
        exit: transitionTime,
      }}
      unmountOnExit
    >
      {(state: string) => (
        <div ref={nodeRef}>
          <Dialog
            isOpen={state !== 'exited'}
            onDismiss={onClose}
            aria-label={label}
          >
            <div
              className={cn(styles.sidebarRoot, classNames)}
              {...swipeHandler}
            >
              <button
                className={cn(styles.overlay, styles[`overlay-${state}`])}
                onClick={onClose}
                type="button"
              >
                {t('close')}
              </button>
              <section
                className={cn(
                  styles.sidebarContainer,
                  styles[`sidebarContainer-${side}`],
                  styles[`sidebarContainer-${side}-${state}`],
                  containerClassNames
                )}
              >
                {children}
              </section>
            </div>
          </Dialog>
        </div>
      )}
    </Transition>
  );
};

export default Sidebar;
